import { PartnerDomain } from '@provision/entity-types';
import { gql, QueryHookOptions, useMutation, useQuery } from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import { isNullish } from 'remeda';
import { QueryVariables, Records } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';

const PARTNER_DOMAIN_FIELDS = gql`
  fragment PartnerDomainFields on PartnerDomain {
    id
    partnerDomainId
    partnerId
    domain
    createdAt
    updatedAt
  }
`;

const GET_PARTNER_DOMAINS_QUERY = gql`
  query GetPartnerDomain($params: QueryParams) @entity(name: "PartnerDomain") {
    getPartnerDomain(queryParams: $params) {
      records {
        ...PartnerDomainFields
      }
    }
  }
  ${PARTNER_DOMAIN_FIELDS}
`;

const CREATE_PARTNER_DOMAIN_MUTATION = gql`
  mutation CreatePartnerDomain($data: PartnerDomainDataInput) {
    createPartnerDomain(data: $data) {
      ...PartnerDomainFields
    }
  }
  ${PARTNER_DOMAIN_FIELDS}
`;

const DELETE_PARTNER_DOMAIN_MUTATION = gql`
  mutation DeletePartnerDomain($conditions: [ConditionInput]) {
    deletePartnerDomain(conditions: $conditions) {
      ...PartnerDomainFields
    }
  }
  ${PARTNER_DOMAIN_FIELDS}
`;

interface GetPartnerDomains {
  getPartnerDomain: Records<PartnerDomain>;
}

export const usePartnerDomains = (
  partnerId: number | null,
  options?: QueryHookOptions<GetPartnerDomains>,
) => {
  const { data, ...rest } = useQuery<GetPartnerDomains, QueryVariables>(
    GET_PARTNER_DOMAINS_QUERY,
    {
      ...options,
      skip: isNullish(partnerId),
      variables: {
        params: {
          filters: [
            {
              attribute: 'partnerId',
              operator: '=',
              value: String(partnerId),
            },
          ],
          sorting: { attribute: 'createdAt', desc: true },
        },
      },
    },
  );

  const partnerDomains = useMemo(() => data?.getPartnerDomain.records || [], [data]);

  return {
    partnerDomains,
    ...rest,
  };
};

export const usePartnerDomain = (partnerId: number | null) => {
  const { showErrorAlert } = useNotifications();

  const [createPartnerDomainMutation] = useMutation<{
    createPartnerDomain: PartnerDomain[];
  }>(CREATE_PARTNER_DOMAIN_MUTATION, {
    onError: (error) => {
      console.error(error);
      const message = 'Something went wrong creating partner domain, please try again';
      showErrorAlert(message);
    },
  });

  const [deletePartnerDomainMutation] = useMutation(DELETE_PARTNER_DOMAIN_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong deleting the partner domain, please try again');
    },
  });

  const createPartnerDomain = useCallback(
    async (payload: Partial<PartnerDomain>) => {
      const createdAt = new Date().toISOString();
      const tempId = `temp-id${createdAt}`;

      const { data } = await createPartnerDomainMutation({
        variables: {
          data: payload,
        },
        optimisticResponse: {
          createPartnerDomain: [
            {
              ...payload,
              id: payload.id || tempId,
              partnerDomainId: payload.id || tempId,
              partnerId,
              createdAt,
              updatedAt: createdAt,
            } as PartnerDomain,
          ],
        },
        update: (cache, { data: response }) => {
          const createdRecord = response?.createPartnerDomain?.[0];
          if (!createdRecord) return;

          cache.updateQuery<GetPartnerDomains, QueryVariables>(
            {
              query: GET_PARTNER_DOMAINS_QUERY,
              variables: {
                params: {
                  filters: [
                    {
                      attribute: 'partnerId',
                      operator: '=',
                      value: String(partnerId),
                    },
                  ],
                  sorting: { attribute: 'createdAt', desc: true },
                },
              },
            },
            (existingData) => {
              if (!existingData?.getPartnerDomain) return;

              const {
                getPartnerDomain: { records },
              } = existingData;

              return {
                getPartnerDomain: {
                  __typename: 'PartnerDomainDataResponse',
                  records: [
                    {
                      ...createdRecord,
                    },
                    ...records,
                  ],
                },
              };
            },
          );
        },
      });

      return data?.createPartnerDomain[0];
    },
    [partnerId, createPartnerDomainMutation],
  );

  const deletePartnerDomain = useCallback(
    (partnerDomainId: string) => {
      return deletePartnerDomainMutation({
        variables: {
          conditions: [
            {
              attribute: 'id',
              operator: '=',
              value: partnerDomainId,
            },
            {
              attribute: 'partnerId',
              operator: '=',
              value: partnerId!.toString(),
            },
          ],
        },
        optimisticResponse: {
          deletePartnerDomain: {
            id: partnerDomainId,
          },
        },
        update(cache) {
          const id = cache.identify({
            __typename: 'PartnerDomain',
            id: partnerDomainId,
          });

          cache.evict({ id });
          cache.gc();
        },
      });
    },
    [deletePartnerDomainMutation, partnerId],
  );

  return {
    createPartnerDomain,
    deletePartnerDomain,
  };
};
